'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Bell, BellRing, Trash2, ArrowUp, ArrowDown, AlertCircle, Loader2, Plus } from 'lucide-react';
import Link from 'next/link';
import { cn } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';
import { getAlerts, createAlert, deleteAlert, type PriceAlert } from '@/lib/api-client';

/** Price-alert panel for the Pro Terminal right rail. Reuses the existing
 *  price-alert system (getAlerts / createAlert / deleteAlert) — the same
 *  endpoints powering the stock page alert bell and /account/alerts. We only
 *  surface the two threshold conditions (above / below) for the create form
 *  since those are the day-trade-relevant ones; pct_change_* alerts created
 *  elsewhere still render correctly in the list. */

type Condition = 'above' | 'below';

function conditionGlyph(c: PriceAlert['condition']) {
  if (c === 'above' || c === 'pct_change_up') return <ArrowUp size={12} className="text-emerald-400" />;
  if (c === 'below' || c === 'pct_change_down') return <ArrowDown size={12} className="text-rose-400" />;
  return null;
}

function conditionLabel(c: PriceAlert['condition']): string {
  switch (c) {
    case 'above': return 'Above';
    case 'below': return 'Below';
    case 'pct_change_up': return 'Up by';
    case 'pct_change_down': return 'Down by';
    default: return c;
  }
}

function formatThreshold(c: PriceAlert['condition'], threshold: number): string {
  if (c === 'pct_change_up' || c === 'pct_change_down') return `${threshold}%`;
  return `$${threshold.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function AlertsPanel(props: { symbol: string; lastPrice: number | null }) {
  const { symbol, lastPrice } = props;
  const { user, loading: authLoading } = useAuth();

  const [alerts, setAlerts] = useState<PriceAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create-form state
  const [condition, setCondition] = useState<Condition>('above');
  const [threshold, setThreshold] = useState('');
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Per-row delete tracking
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAlerts();
      setAlerts(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load alerts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setLoading(false);
      return;
    }
    void load();
  }, [authLoading, user, load]);

  // Prefill threshold near the current price when symbol/price first arrive.
  useEffect(() => {
    if (lastPrice != null && threshold === '') {
      setThreshold(lastPrice.toFixed(2));
    }
    // Only seed once per symbol; clearing the input shouldn't re-fill mid-edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol]);

  const symbolUpper = symbol.trim().toUpperCase();

  // Alerts for the active symbol surface first; everything else is collapsed
  // into an "other symbols" group so the panel stays focused on the chart.
  const { thisSymbol, otherSymbols } = useMemo(() => {
    const mine: PriceAlert[] = [];
    const others: PriceAlert[] = [];
    for (const a of alerts) {
      if (a.symbol.toUpperCase() === symbolUpper) mine.push(a);
      else others.push(a);
    }
    return { thisSymbol: mine, otherSymbols: others };
  }, [alerts, symbolUpper]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (creating) return;
    const value = parseFloat(threshold);
    if (!isFinite(value) || value <= 0) {
      setFormError('Enter a valid price.');
      return;
    }
    if (!symbolUpper) {
      setFormError('No symbol selected.');
      return;
    }
    setCreating(true);
    setFormError(null);
    try {
      const created = await createAlert({ symbol: symbolUpper, condition, threshold: value });
      setAlerts((prev) => [created, ...prev]);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to create alert');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await deleteAlert(id);
      setAlerts((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete alert');
    } finally {
      setDeletingId(null);
    }
  };

  // ── Header ──────────────────────────────────────────────────────────────
  const header = (
    <div className="flex items-center gap-2 px-3 py-2 border-b border-default">
      <Bell size={14} className="text-sky-500" />
      <span className="text-xs font-semibold tracking-wide text-slate-200 uppercase">Price Alerts</span>
      <span className="ml-auto text-[11px] font-mono text-slate-500">{symbolUpper || '—'}</span>
    </div>
  );

  const shell = (children: React.ReactNode) => (
    <div className="flex flex-col bg-surface border border-default rounded-md overflow-hidden">
      {header}
      {children}
    </div>
  );

  // ── Logged-out ──────────────────────────────────────────────────────────
  if (!authLoading && !user) {
    return shell(
      <div className="flex flex-col items-center gap-2 px-4 py-8 text-center">
        <BellRing size={22} className="text-slate-600" />
        <p className="text-sm text-slate-400">Sign in to set price alerts.</p>
        <Link
          href="/login"
          className="mt-1 inline-flex items-center rounded bg-sky-500 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-sky-400"
        >
          Sign in
        </Link>
      </div>,
    );
  }

  return shell(
    <div className="flex flex-col">
      {/* Create form */}
      <form onSubmit={handleCreate} className="flex flex-col gap-2 px-3 py-3 border-b border-default">
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border border-default bg-surface p-0.5">
            <button
              type="button"
              onClick={() => setCondition('above')}
              className={cn(
                'flex items-center gap-1 rounded px-2 py-1 text-[11px] font-medium transition-colors',
                condition === 'above' ? 'bg-emerald-500/15 text-emerald-400' : 'text-slate-500 hover:text-slate-300',
              )}
            >
              <ArrowUp size={11} /> Above
            </button>
            <button
              type="button"
              onClick={() => setCondition('below')}
              className={cn(
                'flex items-center gap-1 rounded px-2 py-1 text-[11px] font-medium transition-colors',
                condition === 'below' ? 'bg-rose-500/15 text-rose-400' : 'text-slate-500 hover:text-slate-300',
              )}
            >
              <ArrowDown size={11} /> Below
            </button>
          </div>
          <div className="relative flex-1">
            <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-500">$</span>
            <input
              type="number"
              step="0.01"
              inputMode="decimal"
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              placeholder={lastPrice != null ? lastPrice.toFixed(2) : '0.00'}
              className="w-full rounded-md border border-default bg-surface py-1.5 pl-5 pr-2 text-sm font-mono text-slate-200 placeholder:text-slate-600 focus:border-sky-500 focus:outline-none"
            />
          </div>
        </div>
        <button
          type="submit"
          disabled={creating || !symbolUpper}
          className="flex items-center justify-center gap-1.5 rounded-md bg-sky-500 py-1.5 text-xs font-medium text-white transition-colors hover:bg-sky-400 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {creating ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
          {creating ? 'Creating…' : `Alert when ${condition} `}
          {!creating && (
            <span className="font-mono">
              {threshold ? `$${threshold}` : (lastPrice != null ? `$${lastPrice.toFixed(2)}` : '')}
            </span>
          )}
        </button>
        {formError && (
          <p className="flex items-center gap-1 text-[11px] text-rose-400">
            <AlertCircle size={11} /> {formError}
          </p>
        )}
      </form>

      {/* List */}
      <div className="max-h-72 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center gap-2 px-3 py-8 text-xs text-slate-500">
            <Loader2 size={14} className="animate-spin" /> Loading alerts…
          </div>
        ) : error ? (
          <div className="flex flex-col items-center gap-2 px-4 py-6 text-center">
            <AlertCircle size={18} className="text-rose-400" />
            <p className="text-xs text-rose-400">{error}</p>
            <button
              onClick={() => void load()}
              className="text-[11px] text-sky-500 hover:text-sky-400"
            >
              Retry
            </button>
          </div>
        ) : alerts.length === 0 ? (
          <div className="flex flex-col items-center gap-1.5 px-4 py-8 text-center">
            <Bell size={20} className="text-slate-700" />
            <p className="text-xs text-slate-500">No active alerts.</p>
            <p className="text-[11px] text-slate-600">Set one above to get notified.</p>
          </div>
        ) : (
          <>
            {thisSymbol.length > 0 && (
              <ul className="divide-y divide-default">
                {thisSymbol.map((a) => (
                  <AlertRow
                    key={a.id}
                    alert={a}
                    deleting={deletingId === a.id}
                    onDelete={() => void handleDelete(a.id)}
                  />
                ))}
              </ul>
            )}

            {otherSymbols.length > 0 && (
              <>
                <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600 bg-surface">
                  Other symbols
                </div>
                <ul className="divide-y divide-default">
                  {otherSymbols.map((a) => (
                    <AlertRow
                      key={a.id}
                      alert={a}
                      showSymbol
                      deleting={deletingId === a.id}
                      onDelete={() => void handleDelete(a.id)}
                    />
                  ))}
                </ul>
              </>
            )}
          </>
        )}
      </div>
    </div>,
  );
}

function AlertRow(props: {
  alert: PriceAlert;
  showSymbol?: boolean;
  deleting: boolean;
  onDelete: () => void;
}) {
  const { alert, showSymbol, deleting, onDelete } = props;
  const triggered = alert.triggeredAt != null;

  return (
    <li className="flex items-center gap-2 px-3 py-2 group">
      <span className="shrink-0">{conditionGlyph(alert.condition)}</span>
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center gap-1.5">
          {showSymbol && (
            <span className="font-mono text-[11px] font-semibold text-slate-300">{alert.symbol}</span>
          )}
          <span className="text-xs text-slate-400">{conditionLabel(alert.condition)}</span>
          <span className="font-mono text-xs text-slate-200">{formatThreshold(alert.condition, alert.threshold)}</span>
        </div>
        <div className="flex items-center gap-1.5">
          {triggered ? (
            <span className="text-[10px] text-amber-400">Triggered</span>
          ) : alert.active ? (
            <span className="flex items-center gap-1 text-[10px] text-emerald-400">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Active
            </span>
          ) : (
            <span className="text-[10px] text-slate-500">Inactive</span>
          )}
          {alert.currentPrice != null && (
            <span className="text-[10px] text-slate-600 font-mono">
              now ${alert.currentPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          )}
        </div>
      </div>
      <button
        onClick={onDelete}
        disabled={deleting}
        title="Delete alert"
        className="shrink-0 rounded p-1 text-slate-600 transition-colors hover:bg-rose-500/10 hover:text-rose-400 disabled:opacity-50"
      >
        {deleting ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
      </button>
    </li>
  );
}
